<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminBlanketVOBTrial"
        label="Blanket VOB Trial Management"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Blanket VOB Trial Management</h1>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search and Filter Section -->
      <div class="search-section">
        <b-field>
          <div class="field-body">
            <b-input
              class="is-expanded"
              :loading="loading"
              v-model="search"
              placeholder="Search by organization name, user email, or user name"
              @input="handleSearch"
            />
            <b-select
              v-model="filterStatus"
              placeholder="Filter by Status"
              :loading="loading"
              @input="handleSearch"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </b-select>
          </div>
        </b-field>
      </div>

      <!-- Add Trial Section -->
      <div class="add-trial-section">
        <h2 class="title is-5">Add Trial Blanket VOB</h2>
        <div class="columns">
          <div class="column is-6">
            <b-field label="Organization">
              <b-select
                v-model="selectedOrganizationId"
                placeholder="Select Organization"
                :loading="loading"
                @input="handleOrganizationChange"
              >
                <option v-for="org in organizationOptions" :value="org.id" :key="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-6">
            <b-field label="User (Optional)">
              <b-select
                v-model="selectedUserId"
                placeholder="Select User (Optional)"
                :loading="loading"
                :disabled="!selectedOrganizationId"
              >
                <option :value="0">All Users in Organization</option>
                <option v-for="user in userOptions" :value="user.id" :key="user.id">
                  {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
        <div class="columns">
          <div class="column is-6">
            <b-field label="Trial Duration (Days)">
              <b-input
                v-model="trialDuration"
                type="number"
                min="1"
                max="365"
                placeholder="30"
              ></b-input>
            </b-field>
          </div>
          <div class="column is-6">
            <b-field label="Number of Inquiries">
              <b-input
                v-model="trialInquiries"
                type="number"
                min="1"
                max="1000"
                placeholder="20"
              ></b-input>
            </b-field>
          </div>
        </div>
        <div class="field">
          <b-button
            type="is-primary"
            :loading="addingTrial"
            :disabled="!canAddTrial"
            @click="addTrial"
          >
            Add Trial Blanket VOB
          </b-button>
        </div>
      </div>

      <hr />

      <!-- Trial Products Table -->
      <div class="trial-products-section">
        <h2 class="title is-5">Existing Trial Products</h2>
        <div v-if="trialProducts.length === 0" class="has-text-grey has-text-centered py-4">
          No trial blanket VOB products found.
        </div>
        <div v-else>
          <b-table
            :data="trialProducts"
            :loading="loading"
            :paginated="!showingAllRecords"
            :per-page="pageSize"
            :current-page="currentPage"
            :total="totalItems"
            @page-change="handlePageChange"
            striped
            hoverable
          >
            <b-table-column field="organization_name" label="Organization" v-slot="props">
              {{ props.row.organization_name }}
            </b-table-column>
            <b-table-column field="user_name" label="User" v-slot="props">
              <span v-if="props.row.user_name">{{ props.row.user_name }}</span>
              <span v-else class="has-text-grey">All Users</span>
            </b-table-column>
            <b-table-column field="period_start" label="Start Date" v-slot="props">
              {{ formatDate(props.row.period_start) }}
            </b-table-column>
            <b-table-column field="period_end" label="End Date" v-slot="props">
              {{ formatDate(props.row.period_end) }}
            </b-table-column>
            <b-table-column field="total_quota" label="Total Inquiries" v-slot="props">
              {{ props.row.total_quota }}
            </b-table-column>
            <b-table-column field="used_quota" label="Used Inquiries" v-slot="props">
              {{ props.row.used_quota }}
            </b-table-column>
            <b-table-column field="remaining_quota" label="Remaining" v-slot="props">
              <span :class="props.row.remaining_quota > 0 ? 'has-text-success' : 'has-text-danger'">
                {{ props.row.remaining_quota }}
              </span>
            </b-table-column>
            <b-table-column field="status" label="Status" v-slot="props">
              <b-tag :type="getStatusType(props.row.status)" size="is-small">
                {{ props.row.status }}
              </b-tag>
            </b-table-column>
            <b-table-column field="created_at" label="Created" v-slot="props">
              {{ formatDate(props.row.created_at) }}
            </b-table-column>
            <b-table-column label="Actions" v-slot="props">
              <b-button
                size="is-small"
                type="is-danger"
                @click="cancelTrial(props.row.id)"
                :disabled="props.row.status === 'cancelled'"
              >
                Cancel
              </b-button>
            </b-table-column>
          </b-table>
          <div v-if="showingAllRecords" class="has-text-grey has-text-centered py-2">
            All trial products are displayed.
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminBlanketVOBTrial',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      addingTrial: false,
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      search: '',
      filterStatus: '',
      selectedOrganizationId: null,
      selectedUserId: 0,
      trialDuration: 30,
      trialInquiries: 20,
      organizationOptions: [],
      userOptions: [],
      trialProducts: [],
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        await Promise.all([this.fetchOrganizations(), this.fetchTrialProducts()]);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load data. Please try again.',
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchOrganizations() {
      const response = await request.get('/admin/organizations');
      this.organizationOptions = response.data.organizations
        .filter((org) => org.has_active_subscription)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    async fetchUsers(organizationId) {
      if (!organizationId) {
        this.userOptions = [];
        return;
      }
      const response = await request.get(`/admin/organizations/${organizationId}/users`);
      this.userOptions = response.data.users.sort((a, b) =>
        a.first_name.localeCompare(b.first_name)
      );
    },
    async fetchTrialProducts() {
      const params = new URLSearchParams({
        page: this.currentPage,
        limit: this.pageSize,
        plan_type: 'blanket_vob',
        product_id: '9', // Trial plan ID
      });

      if (this.search) {
        params.append('search', this.search);
      }
      if (this.filterStatus) {
        params.append('status', this.filterStatus);
      }

      const response = await request.get(`/admin/subscription-products?${params}`);
      this.trialProducts = response.data.products;
      this.totalItems = parseInt(
        response.headers['x-total-count'] || response.data.products.length,
        10
      );
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchTrialProducts();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchTrialProducts();
    },
    async handleOrganizationChange() {
      this.selectedUserId = 0;
      if (this.selectedOrganizationId) {
        await this.fetchUsers(this.selectedOrganizationId);
      } else {
        this.userOptions = [];
      }
    },
    async addTrial() {
      if (!this.canAddTrial) return;

      this.addingTrial = true;
      try {
        const payload = {
          organization_id: this.selectedOrganizationId,
          user_id: this.selectedUserId || null,
          duration_days: parseInt(this.trialDuration),
          inquiries: parseInt(this.trialInquiries),
        };

        await request.post('/admin/blanket-vob-trial', payload);

        this.$buefy.toast.open({
          message: 'Trial blanket VOB product added successfully',
          type: 'is-success',
          duration: 5000,
        });

        // Reset form
        this.selectedOrganizationId = null;
        this.selectedUserId = 0;
        this.trialDuration = 30;
        this.trialInquiries = 20;
        this.userOptions = [];

        // Refresh data
        await this.fetchTrialProducts();
      } catch (error) {
        const message = error.response?.data?.error || 'Failed to add trial blanket VOB product';
        this.$buefy.toast.open({
          message,
          type: 'is-danger',
          duration: 5000,
        });
      } finally {
        this.addingTrial = false;
      }
    },
    async cancelTrial(productId) {
      try {
        await request.post(`/admin/subscription-products/${productId}/cancel`);

        this.$buefy.toast.open({
          message: 'Trial cancelled successfully',
          type: 'is-success',
          duration: 5000,
        });

        await this.fetchTrialProducts();
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to cancel trial',
          type: 'is-danger',
          duration: 5000,
        });
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString();
    },
    getStatusType(status) {
      switch (status) {
        case 'active':
          return 'is-success';
        case 'cancelled':
          return 'is-danger';
        case 'expired':
          return 'is-warning';
        default:
          return 'is-light';
      }
    },
  },
  computed: {
    canAddTrial() {
      return (
        this.selectedOrganizationId &&
        this.trialDuration > 0 &&
        this.trialInquiries > 0 &&
        !this.addingTrial
      );
    },
    showingAllRecords() {
      return this.trialProducts.length === this.totalItems;
    },
  },
};
</script>

<style scoped>
.search-section {
  margin-bottom: 1.5rem;
}
.add-trial-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: #f5f5f5;
  border-radius: 6px;
}
.trial-products-section {
  margin-top: 2rem;
}
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
  margin-right: 1rem;
}
</style>
